import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import { useTranslation } from 'react-i18next';
import { PiBriefcaseLight, PiPlusLight, PiUsersLight, PiPaperclipLight, PiChatCircleTextLight, PiTrashLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import ResourceTrash from '../../RecycleBin/ResourceTrash';
import Header from '../../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../../shared/TailwindComponents/Table';
import Badge from '../../shared/TailwindComponents/Badge';
import ProfileImage from '../../Header/ProfileImage';
import Button from '../../shared/TailwindComponents/Button';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import { fetchAllTeams, deleteTeam } from '../../../store/actions/organization/teamActions';
import TeamForm from './TeamForm';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { Tooltip } from '@mui/material';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, teamListTourSteps } from '../../shared/FeatureTour';

interface TeamRow {
  id: string;
  Name: React.ReactNode;
  'Practice Areas': React.ReactNode;
  'Members': React.ReactNode;
  'Documents': React.ReactNode;
  'Feed': React.ReactNode;
  Description: string;
  Status: React.ReactNode;
  Actions?: React.ReactNode;
}

interface Team {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  member_count: number;
  document_count: number;
  feed_item_count: number;
  members: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role?: string;
    profile_picture?: string | null;
  }>;
  practice_area?: string;
  practice_area_id?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  practice_area_ids?: number[];
  department?: string;
  department_id?: string;
  created_at?: string;
}

const TeamGridItem: React.FC<{ team: any; onClick: (id: string) => void }> = ({ team, onClick }) => {
  const { t } = useTranslation();
  const handleClick = () => {
    onClick(team.id);
  };

  return (
    <div
      className="relative cursor-pointer bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-3"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-md text-gray-900 dark:text-white font-semibold truncate">{team.name}</h3>
        {team.is_active !== undefined && (
          <Badge color={team.is_active ? 'green' : 'gray'}>
            {team.is_active ? t('Active') : t('Inactive')}
          </Badge>
        )}
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
        {team.description || t('No description')}
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <PiPaperclipLight className="w-4 h-4" />
          <span>{team.document_count || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <PiChatCircleTextLight className="w-4 h-4" />
          <span>{team.feed_item_count || 0}</span>
        </div>
      </div>

      {team.members && team.members.length > 0 && (
        <div className="flex -space-x-2">
          {team.members.slice(0, 3).map((member: any, index: number) => (
            <div key={index} className="relative" title={`${member.first_name} ${member.last_name}`}>
              <ProfileImage
                profilePicture={member.profile_picture || null}
                email={member.email}
                initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
                size="w-8 h-8"
                firstName={member.first_name}
                lastName={member.last_name}
              />
            </div>
          ))}
          {team.members.length > 3 && (
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-700 dark:text-gray-200 font-semibold">
              +{team.members.length - 3}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TeamList: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditTarget, setAuditTarget] = useState<{ id: string; name: string } | null>(null);
  const itemsPerPage = 50;
  const [displayCount, setDisplayCount] = useState(itemsPerPage);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'team-list',
    steps: teamListTourSteps,
    readyToShow: !loading && !isAddingTeam && !showTrash,
    autoStartDelay: 600,
  });

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      setIsAddingTeam(true);
    } else {
      setIsAddingTeam(false);
    }
  }, [location]);

  useEffect(() => {
    let isMounted = true;

    const loadTeams = async () => {
      setLoading(true);
      setError('');
      setIsFetching(true);
      try {
        const result = await dispatch(fetchAllTeams() as any);
        if (isMounted && Array.isArray(result)) {
          setTeams(result);
        } else if (isMounted) {
          setTeams([]);
          setError(t('Failed to load teams'));
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Error loading teams:', err);
          setError(t('Failed to load teams'));
          setTeams([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsFetching(false);
        }
      }
    };

    loadTeams();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredTeams = useMemo(() => {
    if (!Array.isArray(teams)) {
      return [];
    }
    if (!searchTerm.trim()) {
      return teams;
    }

    const lowerQuery = searchTerm.toLowerCase();
    return teams.filter(
      (team) =>
        team.name?.toLowerCase().includes(lowerQuery) ||
        team.description?.toLowerCase().includes(lowerQuery)
    );
  }, [teams, searchTerm]);

  // Client-side pagination: show first N items
  const displayedTeams = useMemo(() => {
    return filteredTeams.slice(0, displayCount);
  }, [filteredTeams, displayCount]);

  const hasMore = displayCount < filteredTeams.length;

  const loadMore = useCallback(() => {
    setDisplayCount(prev => prev + itemsPerPage);
  }, []);

  // Reset display count when search or filters change
  useEffect(() => {
    setDisplayCount(itemsPerPage);
  }, [searchTerm, selectedFilters]);

  const handleCloseAddTeam = async () => {
    setIsAddingTeam(false);
    history.push('/teams');
    setLoading(true);
    setError('');
    setIsFetching(true);
    try {
      const result = await dispatch(fetchAllTeams() as any);
      if (Array.isArray(result)) {
        setTeams(result);
      } else {
        setTeams([]);
        setError(t('Failed to load teams'));
      }
    } catch (err: any) {
      console.error('Error loading teams:', err);
      setError(t('Failed to load teams'));
      setTeams([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  };

  const handleSearchChange = (query: string) => {
    setSearchTerm(query);
  };

  const columns = ['Name', 'Practice Areas', 'Members', 'Documents', 'Feed', 'Description', 'Status', 'Actions'];

  const teamTableActions = [
    {
      label: t('Activity Log'),
      onClick: (row: any) => {
        const team = teams.find((t) => t.id === row.id);
        const name = team?.name || '';
        setAuditTarget({ id: row.id, name });
        setShowAuditLog(true);
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete Team'),
      onClick: (row: any) => {
        dispatch(showModal({
          type: 'confirmation',
          message: t('Delete Team'),
          subMsg: t('This team and all associated data will be permanently deleted. This action cannot be undone.'),
          icon: <PiTrashLight className='w-6 h-6 text-red-500' />,
          isDest: true,
          showModal: true,
          onConfirm: async () => {
            await dispatch(deleteTeam(row.id) as any);
            setLoading(true);
            setError('');
            setIsFetching(true);
            try {
              const updated = await dispatch(fetchAllTeams() as any);
              if (Array.isArray(updated)) {
                setTeams(updated);
              }
            } catch (err: any) {
              console.error('Error loading teams:', err);
              setError(t('Failed to load teams'));
            } finally {
              setLoading(false);
              setIsFetching(false);
              dispatch(hideModal());
            }
          },
          onCancel: () => dispatch(hideModal()),
        }));
      },
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const data = useMemo<TeamRow[]>(() => {
    if (!Array.isArray(displayedTeams)) {
      return [];
    }
    return displayedTeams.map((team): TeamRow => {
      return {
        id: team.id,
        Name: (
          <div className="flex items-center space-x-3">
            <div>
              <span className={!team.is_active ? 'text-gray-500 dark:text-gray-400' : ''}>{team.name}</span>
            </div>
          </div>
        ),
        'Practice Areas': team.practice_areas && team.practice_areas.length > 0 ? (
          <div className="flex flex-wrap gap-1 items-center">
            {team.practice_areas.slice(0, 2).map((pa) => (
              <Badge key={pa.id} color="blue">
                {pa.name}
              </Badge>
            ))}
            {team.practice_areas.length > 2 && (
              <Tooltip
                title={team.practice_areas.slice(2).map((pa) => pa.name).join(', ')}
                arrow
                placement="top"
              >
                <span className="text-xs text-gray-500 dark:text-gray-400 cursor-default font-medium">
                  {t('+{{count}} more', { count: team.practice_areas.length - 2 })}
                </span>
              </Tooltip>
            )}
          </div>
        ) : team.practice_area ? (
          <span className="text-xs text-gray-600 dark:text-gray-400">{team.practice_area}</span>
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-sm">—</span>
        ),
        'Members': team.members && team.members.length > 0 ? (
          <div className="flex -space-x-2">
            {team.members.slice(0, 4).map((member) => (
              <div key={member.id} className="relative" title={`${member.first_name} ${member.last_name}`}>
                <ProfileImage
                  profilePicture={member.profile_picture || null}
                  email={member.email}
                  initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
                  size="w-6 h-6"
                  firstName={member.first_name}
                  lastName={member.last_name}
                />
              </div>
            ))}
            {team.members.length > 5 && (
              <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-600 dark:text-gray-300">
                +{team.members.length - 4}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-sm">—</span>
        ),
        'Documents': (
          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
            <PiPaperclipLight className="w-4 h-4" />
            <span>{team.document_count || 0}</span>
          </div>
        ),
        'Feed': (
          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
            <PiChatCircleTextLight className="w-4 h-4" />
            <span>{team.feed_item_count || 0}</span>
          </div>
        ),
        Description: team.description || '-',
        Status: (
          <Badge color={team.is_active ? 'green' : 'gray'}>
            {team.is_active ? t('Active') : t('Inactive')}
          </Badge>
        ),
      };
    });
  }, [displayedTeams, t]);

  if (isAddingTeam) {
    return (
      <div className='h-screen'>
        <TeamForm onSuccess={handleCloseAddTeam} onCancel={handleCloseAddTeam} />
      </div>
    );
  }

  return (
    <>
      <div data-tour-id="team-list-header">
      <Header
        actions={[
          {
            label: t('Add Team'),
            icon: <PiPlusLight className='w-4 h-4' />,
            onClick: () => history.push('/teams?add=true'),
            requiresSubscription: false,
          },
        ]}
      />
      </div>
      <div data-tour-id="team-list-filters">
      <FilterBarWithTabs
        filters={[]}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onClearAllFilters={() => setSelectedFilters({})}
        viewMode={viewMode}
        setViewMode={setViewMode}
        actions={showTrash ? trashBulkActions : []}
        selectionCount={showTrash ? trashSelectedCount : undefined}
        recycleBinEnabled
        recycleBinActive={showTrash}
        recycleBinLabel={t('Teams')}
        onRecycleBinToggle={(active) => {
          if (!active) {
            dispatch(fetchAllTeams() as any).then((result: any) => {
              if (Array.isArray(result)) setTeams(result);
            });
          }
          setShowTrash(active);
        }}
      />
      </div>

      {showTrash ? (
        <ResourceTrash
          entityType="team"
          entityLabel="teams"
          emptyIcon={<PiUsersLight />}
          onItemRestored={() => dispatch(fetchAllTeams() as any).then((result: any) => { if (Array.isArray(result)) setTeams(result); })}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      <div className="relative">
        <div data-tour-id="team-list-content" className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none" />
        {error && (
          <div className="mb-6 p-4 mx-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}


        {viewMode === 'grid' ? (
          <div className="px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-auto max-h-[calc(100vh-200px)] custom-scrollbar-main">
              {isFetching && teams.length === 0 ? (
                <SkeletonClientItem viewMode="grid" count={3} />
              ) : displayedTeams.length > 0 ? (
                displayedTeams.map((team) => (
                  <TeamGridItem
                    key={team.id}
                    team={{
                      id: team.id,
                      name: team.name,
                      description: team.description,
                      is_active: team.is_active,
                      members: team.members,
                      document_count: team.document_count,
                      feed_item_count: team.feed_item_count,
                    }}
                    onClick={(id) => history.push(`/teams/${id}`)}
                  />
                ))
              ) : null}
            </div>
            {hasMore && displayedTeams.length > 0 && (
              <div className="flex justify-center py-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="small"
                  onClick={loadMore}
                >
                  {t('Load More')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            {isFetching && teams.length === 0 ? (
              <SkeletonClientItem viewMode="table" count={2} />
            ) : (
              <Table
                columns={columns}
                data={data}
                onRowClick={(row) => {
                  const team = displayedTeams.find((t) => t.id === row.id);
                  if (team) {
                    history.push(`/teams/${team.id}`);
                  }
                }}
                actions={teamTableActions}
                hasMore={hasMore}
                isLoadingMore={false}
                onLoadMore={loadMore}
                total_items={filteredTeams.length}
                total_showing_items={displayedTeams.length}
                setIsShowing={true}
                showingLabel={t('teams')}
                resizableColumns={true}
                defaultColumnWidths={{
                  'Name': 200,
                  'Practice Areas': 200,
                  'Members': 120,
                  'Documents': 110,
                  'Feed': 100,
                  'Description': 240,
                  'Status': 120,
                  'Actions': 60,
                }}
                emptyState={{
                  icon: <PiBriefcaseLight />,
                  headline: searchTerm ? t('No results found.') : t('You have no teams added yet.'),
                  subtext: searchTerm
                    ? t('Try adjusting your search to find what you\'re looking for.')
                    : t('Start by creating a team to get organized.'),
                  button: {
                    label: searchTerm ? t('Clear Search') : t('Create Team'),
                    onClick: searchTerm ? () => setSearchTerm('') : () => history.push('/teams?add=true'),
                  },
                }}
              />
            )}
          </>
        )}
      </div>
      )}

      <FeatureTour
        steps={teamListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />

      {/* Audit Log Side Panel with Backdrop */}
      {showAuditLog && auditTarget && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
            onClick={() => setShowAuditLog(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType="Team"
              entityId={auditTarget.id}
              entityName={auditTarget.name}
              onClose={() => {
                setShowAuditLog(false);
                setAuditTarget(null);
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default TeamList;
